import React, { useMemo, useEffect, useState, useCallback } from "react";
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
} from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";
import {
  ChevronLeft,
  ChevronRight,
  Launch,
  OpenInNew,
} from "@mui/icons-material";
import "./NCXComponents.styles.css";
import { Button, Typography } from "@mui/material";
import { s360ConfigService } from "../../../Services/S360Config";

const MediaRenderer = ({ src, fallback }) => {
  const [mediaType, setMediaType] = useState("unknown");
  const transformedSrc = src
    .replace("/download/storage/v1/b", "")
    .replace("/o", "");
  useEffect(() => {
    const url = new URL(src, window.location.origin);
    const extension = url.pathname.split(".").pop().toLowerCase();

    const SUPPORTED_TYPES = {
      image: ["jpg", "jpeg", "png", "gif", "webp"],
      video: ["mp4", "avi", "mov", "webm", "mkv"],
      pdf: ["pdf"],
      audio: ["mp3", "wav", "ogg", "flac", "aac"],
    };

    if (SUPPORTED_TYPES.image.includes(extension)) return setMediaType("image");
    if (SUPPORTED_TYPES.video.includes(extension)) return setMediaType("video");
    if (SUPPORTED_TYPES.pdf.includes(extension)) return setMediaType("pdf");
    if (SUPPORTED_TYPES.audio.includes(extension)) return setMediaType("audio");

    setMediaType("unsupported");
  }, [src]);

  return (
    <div className="aspect-ratio-box">
      {mediaType === "image" && <img src={transformedSrc} alt="content" onClick={() => window.open(transformedSrc, '_blank')} />}
      {mediaType === "video" && <video src={transformedSrc} controls onClick={() => window.open(transformedSrc, '_blank')} />}
      {mediaType === "pdf" && (
        <object
          data={transformedSrc}
          type="application/pdf"
          width="100%"
          height="100%"
          onClick={() => window.open(transformedSrc, '_blank')}
        >
          <img src={fallback} alt="Fallback media" width={100} />
        </object>
      )}
      {mediaType === "audio" && <audio controls src={transformedSrc} onClick={() => window.open(transformedSrc, '_blank')} />}
      {mediaType === "unsupported" && (
        <img src={fallback} alt="Fallback media" />
      )}
    </div>
  );
};

const MediaComponent = React.memo(
  function MediaComponent({ mediaUrl, subTitle, title, actions, link, id }) {
    const primaryColorSelectorFromConfig = useMemo(
      () => s360ConfigService.config?.configDetails?.primaryFontColor ?? "#fff",
      []
    );
    const primaryFontColorSelectorFromConfig = useMemo(
      () => s360ConfigService.config?.configDetails?.primaryColor ?? "#015feb",
      []
    );

    const checkUrlValidAndOpen = useCallback((string) => {
      let url;

      try {
        url = new URL(string);
      } catch (_) {
        return false;
      }

      window.open(string, "_blank");
    }, []);

    const containerStyle = useMemo(
      () => ({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
      }),
      []
    );

    const titleContainerStyle = useMemo(
      () => ({
        display: "flex",
        alignItems: "center",
      }),
      []
    );

    return (
      <div
        className="slideImageContainer"
        key={`${id}-ncxcarouselslideimagecontainer`}
      >
        <div
          style={containerStyle}
          key={`${id}-ncxcarouselslidetitlecontainer`}
        >
          <div
            style={titleContainerStyle}
            key={`${id}-ncxcarouselslidetitlecontainer`}
          >
            <div className="slideTitleContainer">
              <Typography
                variant="h5"
                className="slideTitle"
                sx={{ fontWeight: "700" }}
                key={`${id}-ncxcarouselslidesubtitle`}
              >
                {title}
              </Typography>
              <Typography
                variant="caption"
                fontSize="xx-small"
                className="slideTitle"
                key={`${id}-ncxcarouselslidetitle`}
              >
                {subTitle}
              </Typography>
            </div>
          </div>
          <a href={link} target="_blank" rel="noreferrer">
            <OpenInNew />
          </a>
        </div>
        {mediaUrl && (
          <MediaRenderer
            src={mediaUrl}
            fallback="https://static-00.iconduck.com/assets.00/image-not-supported-icon-1024x1024-2s23yx2y.png"
            aspectRatio="1/1"
          />
        )}
        <div
          className="slideActionsContainer"
          key={`${id}-ncxcarouselslideslideactionscontainer`}
        >
          {actions.map(({ text, buttonUrl }, index) => (
            <Button
              variant="outlined"
              onClick={(_) => checkUrlValidAndOpen(buttonUrl)}
              sx={{
                backgroundColor: `${primaryFontColorSelectorFromConfig} !important`,
                color: `${primaryColorSelectorFromConfig} !important`,
                borderColor: `${primaryFontColorSelectorFromConfig} !important`,
                "&:hover": {
                  color: `${primaryFontColorSelectorFromConfig} !important`,
                  borderColor: `${primaryFontColorSelectorFromConfig} !important`,
                  backgroundColor: `${primaryColorSelectorFromConfig} !important`,
                },
                width: "100%",
                fontSize: "12px",
                fontWeight: "600",
              }}
              key={`${id}-ncxcarouselslideslidebuttonurl-${text}-${index}`}
            >
              <Launch sx={{ marginRight: "4px", marginBottom: "2px" }} />
              {text}
            </Button>
          ))}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.mediaUrl === nextProps.mediaUrl &&
      prevProps.subTitle === nextProps.subTitle &&
      prevProps.title === nextProps.title &&
      JSON.stringify(prevProps.actions) === JSON.stringify(nextProps.actions) &&
      prevProps.link === nextProps.link &&
      prevProps.id === nextProps.id
    );
  }
);

const NCXCarousel = React.memo(
  function NCXCarouselComponent(props) {
    const { data, id } = props;

    const primaryColorSelectorFromConfig = useMemo(
      () => s360ConfigService.config?.configDetails?.primaryFontColor ?? "#fff",
      []
    );
    const primaryFontColorSelectorFromConfig = useMemo(
      () => s360ConfigService.config?.configDetails?.primaryColor ?? "#015feb",
      []
    );

    const sliderContainerStyle = useMemo(
      () => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "0 0 0 8px",
      }),
      []
    );

    const sliderControllerStyle = useMemo(
      () => ({
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        width: "100%",
      }),
      []
    );

    return (
      <div className="sliderContainer" key={`${id}-ncxcarouselsildercontainer`}>
        <CarouselProvider
          naturalSlideWidth={100}
          isIntrinsicHeight
          totalSlides={data.data.length}
          className="sliderViewContainer"
          key={`${id}-ncxcarouselsilderviewcontainer`}
        >
          <div
            style={sliderContainerStyle}
            key={`${id}-ncxcarouselsildercontroller`}
          >
            <Typography
              variant="h5"
              sx={{ lineHeight: "32px", fontWeight: "700" }}
            >
              {data.carouselTitle ?? "Hello world"}
            </Typography>
          </div>
          {data.orientation === "horizontal" && (
            <div style={{ display: "flex", width: "100%" }}>
              {data.data?.length > 1 && (
                <ButtonBack
                  className="slideControllerButtons"
                  key={`${id}-ncxcarouselslidecontrollerbuttonback`}
                  style={{ position: "absolute", zIndex: "10", top: "50%" }}
                >
                  <ChevronLeft
                    fontSize="large"
                    key={`${id}-ncxcarouselchevronleft`}
                    style={{ background: "#ddd", borderRadius: "100%" }}
                  />
                </ButtonBack>
              )}

              <Slider key={`${id}-ncxcarouselsilder`}>
                {data.data.map((mediaDetails, index) => (
                  <Slide index={index} key={index}>
                    <MediaComponent {...mediaDetails} id={id} />
                  </Slide>
                ))}
              </Slider>
              {data.data?.length > 1 && (
                <ButtonNext
                  className="slideControllerButtons"
                  key={`${id}-ncxcarouselslidecontrollerbuttonnext`}
                  style={{
                    position: "absolute",
                    zIndex: "10",
                    right: "0%",
                    top: "50%",
                  }}
                >
                  <ChevronRight
                    fontSize="large"
                    key={`${id}-ncxcarouselslidecontrollerright`}
                    style={{ background: "#ddd", borderRadius: "100%" }}
                  />
                </ButtonNext>
              )}
            </div>
          )}
          {data.orientation === "vertical" && (
            <div style={sliderControllerStyle}>
              {data.data.map((mediaDetails, index) => (
                <MediaComponent {...mediaDetails} key={index} id={id} />
              ))}
            </div>
          )}
        </CarouselProvider>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
      prevProps.id === nextProps.id
    );
  }
);

export default NCXCarousel;
